/**
 * Browser language detection utilities.
 *
 * Shared helpers so that language-sensitive features (CMS, i18n, popups)
 * all use a consistent source of truth for the browser language.
 */

import { SupportedLanguage } from '../../common/cms/cmsLanguageConstants';
import { getLanguageForCMS } from '../../common/cms/languageValidationUtils';
import { DEFAULT_APP_LANGUAGE_CODE, SUPPORTED_APP_LANGUAGE_CODES } from '../user/account/accountModal/localisation/localizationConstants';

/**
 * Gets the browser's language preference (full locale string)
 * e.g. "es-ES", "en-US", or null if not available.
 */
const getBrowserLanguage = (): string | null => {
    try {
        return navigator.language || navigator.languages?.[0] || null;
    } catch {
        return null;
    }
};

/**
 * Returns the browser language code (e.g. "en", "es") in lowercase,
 * or null if it can't be determined.
 */
export const getBrowserLanguageCode = (): string | null => {
    const browserLocale = getBrowserLanguage();
    if (!browserLocale) return null;

    try {
        const intlLocale = new Intl.Locale(browserLocale);
        return intlLocale.language.toLowerCase();
    } catch {
        const [languageCode] = browserLocale.split(/[-_]/);
        return languageCode ? languageCode.toLowerCase() : null;
    }
};

/**
 * Returns a supported, non-default app language code based on the browser language,
 * or null if there is no suitable suggestion.
 */
export const getSuggestedLocaleFromBrowser = (): string | null => {
    const browserLanguageCode = getBrowserLanguageCode();
    if (!browserLanguageCode) return null;

    const normalised = browserLanguageCode.toLowerCase();

    const supportedNonDefaultLocale = SUPPORTED_APP_LANGUAGE_CODES.find(
        (code) => code.toLowerCase() === normalised && code !== DEFAULT_APP_LANGUAGE_CODE,
    );

    return supportedNonDefaultLocale || null;
};

/**
 * Gets language for CMS requests, prioritizing user preference over browser language.
 * This is used to resolve a SupportedLanguage for Prismic.
 */
export const getLanguageForCMSRequest = (userLanguagePreference?: string | null): SupportedLanguage => {
    return getLanguageForCMS(userLanguagePreference, getBrowserLanguage());
};
